import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

/**
 * Universal contact link.
 *
 * ANY contact name shown anywhere in the app should render through this
 * component. Tapping it deep-links to /dashboard?focus=<user_id>, which
 * the Dashboard translates into `focusUser` → the MapView smoothly flies
 * to that person and highlights their marker (source: `highlight`).
 *
 * Props
 *   userId   : Proximity user_id of the person (required)
 *   name     : Display name (required)
 *   className: optional extra classes for the label element
 *   avatar   : optional avatar node to render before the name
 *   showChevron : boolean — show the "→" ChevronRight to communicate tappability
 *   testId   : optional data-testid override
 *   onNavigate  : optional side-effect callback (analytics etc.)
 */
export default function ContactLink({
  userId,
  name,
  className = "",
  avatar = null,
  showChevron = true,
  testId,
  onNavigate,
  children,
}) {
  if (!userId) {
    // Fallback: still render a name so we never regress the UI when data is partial.
    return <span className={className}>{children ?? name}</span>;
  }
  return (
    <Link
      to={`/dashboard?focus=${encodeURIComponent(userId)}`}
      data-testid={testId || `contact-link-${userId}`}
      onClick={onNavigate}
      className={`inline-flex items-center gap-1.5 hover:text-[#007AFF] transition-colors ${className}`}
    >
      {avatar}
      <span className="truncate">{children ?? name}</span>
      {showChevron && <ChevronRight size={12} className="text-zinc-500 shrink-0" />}
    </Link>
  );
}
